import { useState } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import {
  RangeSlider,
  RangeSliderValue,
} from '../../experimental/atoms/range-slider';
import { cn } from '../../utils/cn';

type RangeFilterProps = {
  label: string;
  /** The bounds in the URL or state, or undefined when the filter is unset. */
  value?: RangeSliderValue;
  min: number;
  max: number;
  step: number;
  formatValue?: (value: number) => string;
  /** A short line under the label saying what the scale measures. */
  caption?: string;
  /** The sentence under the slider for a range, or none when unset. */
  describeValue?: (value?: RangeSliderValue) => string;
  disabled?: boolean;
  permanent?: boolean;
  showClear?: boolean;
  dataTestId?: string;
  onCommit: (value: RangeSliderValue) => void;
  onClear: () => void;
  onClose?: () => void;
};

const BUTTON_CLASS_NAME =
  'flex items-center rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100';

const isSameRange = (left?: RangeSliderValue, right?: RangeSliderValue) =>
  left?.[0] === right?.[0] && left?.[1] === right?.[1];

/**
 * The filter-bar chip for a range. Dragging moves a local draft and only the
 * released value is written, so the list is fetched once per change.
 */
export const RangeFilter = ({
  label,
  value,
  min,
  max,
  step,
  formatValue,
  caption,
  describeValue,
  disabled,
  permanent,
  showClear,
  dataTestId,
  onCommit,
  onClear,
  onClose,
}: RangeFilterProps) => {
  const { t } = useTranslation('designSystem');
  const [draft, setDraft] = useState<RangeSliderValue>();
  const [previousValue, setPreviousValue] = useState(value);

  // The written value arrives a render after the commit, so the draft is
  // kept until then to stop the thumbs jumping back.
  if (!isSameRange(previousValue, value)) {
    setPreviousValue(value);
    setDraft(undefined);
  }

  return (
    <div
      data-testid={dataTestId}
      className="flex min-w-64 flex-col gap-1 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm antialiased dark:border-gray-600 dark:bg-gray-800"
    >
      <div className="flex items-center gap-1">
        <span
          className={cn(
            'flex-1 font-medium text-black dark:text-white',
            disabled && 'text-content-muted dark:text-gray-400',
          )}
        >
          {label}
        </span>
        {onClose && !permanent && (
          <button
            type="button"
            aria-label={t('removeLabel', { label })}
            data-testid={dataTestId && `${dataTestId}-close-button`}
            className={BUTTON_CLASS_NAME}
            onClick={onClose}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
        {showClear && value && (
          <button
            type="button"
            aria-label={t('clear')}
            data-testid={dataTestId && `${dataTestId}-clear-button`}
            className={BUTTON_CLASS_NAME}
            onClick={onClear}
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        )}
      </div>
      <RangeSlider
        value={draft ?? value ?? [min, max]}
        min={min}
        max={max}
        step={step}
        label={label}
        formatValue={formatValue}
        caption={caption}
        description={describeValue?.(draft ?? value)}
        disabled={disabled}
        onValueChange={setDraft}
        onValueCommit={onCommit}
      />
    </div>
  );
};
